import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { 
    collectionGroup, 
    query, 
    where, 
    onSnapshot, 
    doc, 
    updateDoc, 
    serverTimestamp 
} from "firebase/firestore";

// CORRECCIÓN: Rutas relativas recalibradas de forma precisa a la profundidad de la carpeta Renovaciones
import { db } from '../../../../firebase/config';
import { useAuth } from '../../../hooks/useAuth'; 

import { useTableState } from '../hooks/useTableState';
import { useDataHydration } from '../hooks/useDataHydration';

/**
 * useRenovacionesTable - Hook de negocio encargado del estado del Tablero de Renovaciones.
 */
export const useRenovacionesTable = () => {
    const { user, profile } = useAuth();
    
    // --- ESTADO DE BASE DE DATOS CRUDA ---
    const [polizas, setPolizas] = useState([]);
    const [loading, setLoading] = useState(true);

    // --- ESTADOS DE CONTROL DE MODALES ---
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showRenewModal, setShowRenewModal] = useState(false);
    const [showDebtModal, setShowDebtModal] = useState(false);
    const [showFichaModal, setShowFichaModal] = useState(false); 
    const [activePoliza, setActivePoliza] = useState(null);

    // REFS DE CONCURRENCIA
    const activeItemPathRef = useRef(null);

    // --- 1. HOOK DE HIDRATACIÓN EN BACKGROUND (useDataHydration) ---
    const { clientsCache } = useDataHydration(polizas);

    // --- 2. LISTENER EN TIEMPO REAL CON FILTRADO DE REGLAS DE NEGOCIO ---
    useEffect(() => {
        const today = new Date(); today.setUTCHours(0, 0, 0, 0); 
        const sixtyAgoStr = new Date(new Date().setDate(today.getDate() - 60)).toISOString().split('T')[0];
        const sixtyFromStr = new Date(new Date().setDate(today.getDate() + 60)).toISOString().split('T')[0];
        
        const q = query(
            collectionGroup(db, 'polizas'), 
            where('estadoEmision', 'in', ['NO INICIADA', 'VIGENTE', 'VENCIDA'])
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const rawData = snapshot.docs.map(docSnap => ({ 
                id: docSnap.id, 
                path: docSnap.ref.path, 
                clienteId: docSnap.ref.path.split('/')[3], 
                ...docSnap.data() 
            }));

            const processed = [];
            const normalize = (str) => str ? str.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toUpperCase().trim() : "";
            const subriesgosCaucionPermitidos = [
                'ALQUILER PARTICULAR', 'ALQUILER COMERCIAL', 'ADMINISTRADOR DE SOCIEDADES (IGJ)', 
                'AMBIENTAL', 'OTRAS', 'ADUANERA', 'EMPRESAS DE VIAJES Y TURISMO', 'EJERCICIO DE LA ACTIVIDAD Y/O PROFESIÓN', 'MARTILLERO'
            ];

            rawData.forEach(p => {
                // Filtros de exclusión estrictos por negocio
                if (['RENOVADA', 'NO RENUEVA', 'ANULADA'].includes(p.estadoGestionRenovacion)) return;
                if (p.tipoFacturacion?.toUpperCase() === 'PERIODO CORTO') return;
                if (p.tipoRenovacion?.toUpperCase() === 'NO RENOVABLE' || p.tipoRenovacion?.toUpperCase() === 'TEMPORAL') return;
                if (normalize(p.riesgo) === 'CAUCION' && !subriesgosCaucionPermitidos.includes(normalize(p.subriesgo))) return;

                // Ventana temporal de vencimientos (entre -60 y +60 días)
                if (p.finVigencia >= sixtyAgoStr && p.finVigencia <= sixtyFromStr) {
                    processed.push({ ...p, vencimientoDate: p.finVigencia });
                }
            });

            setPolizas(processed);
            setLoading(false);
        }, (error) => console.error("Error cargando polizas para renovacion:", error));

        return () => unsubscribe();
    }, []);

    // --- 3. MAPEO E HISTORIAL DE ENTIDADES (WATERFALL FALLBACK) ---
    const itemsTable = useMemo(() => {
        return polizas.map(p => {
            const c = clientsCache[p.clienteId] || {};
            let displayNombre = `${(p.clienteNombre || '').trim()} ${(p.clienteApellido || '').trim()}`.trim();
            if (!displayNombre) {
                if (c.persona === 'JURIDICA') displayNombre = (c.nombre || '').trim().toUpperCase();
                else displayNombre = `${c.nombre || ''} ${c.apellido || ''}`.trim().toUpperCase();
            }
            if (!displayNombre) displayNombre = 'S/D';
            
            const displayDoc = p.clienteCuit || p.clienteDni || c.cuit || c.dni || '-';

            return { 
                ...p, 
                displayNombre, 
                displayDoc,
                canal: p.canal || '',
                canalNombre: p.canalNombre || '',
                subcanalNombre: p.subcanalNombre || '', // ◄ LECTURA EXCLUSIVA Y DIRECTA DE subcanalNombre
                estadoGestion: p.estadoGestionRenovacion || 'PENDIENTE' // Asignación para mapear filtros de useTableState
            };
        });
    }, [polizas, clientsCache]);

    // --- 4. PREVENCIÓN DE PARPADEO (FUC) ---
    // Mantenemos el spinner si las pólizas están cargadas pero aún no resolvieron sus clientes de caché
    const needsHydration = useMemo(() => {
        if (polizas.length === 0) return false;
        return polizas.some(p => !clientsCache[p.clienteId]);
    }, [polizas, clientsCache]);

    // --- 5. HOOK DE CONTROL SÍNCRONO DE TABLAS DE GESTIÓN (useTableState) ---
    const tableState = useTableState({
        data: itemsTable,
        initialSort: { key: 'vencimientoDate', direction: 'ascending' },
        initialColumns: {
            vencimientoDate: true, nPoliza: true, compania: true,
            displayNombre: true, displayDoc: true, riesgo: true, formaPago: true,
            estadoGestion: true, gestionadoPor: true, canal: true
        },
        recordsPerPage: 100
    });

    // Desbloqueo manual/automático
    const unlockPoliza = useCallback(async (path) => {
        if (!path) return;
        try { 
            await updateDoc(doc(db, path), { bloqueo: null }); 
            if (activeItemPathRef.current === path) activeItemPathRef.current = null;
        } catch (e) { console.error("Error al desbloquear póliza para renovación:", e); }
    }, []);

    // --- 6. INTERCEPTOR DE CONTROLADORES DE ACCIÓN ---
    const handleAction = useCallback(async (poliza, type) => {
        setActivePoliza(poliza);
        if (type === 'ficha') { 
            setShowFichaModal(true); 
            return; 
        }
        if (type === 'view') { 
            setShowDetailModal(true); 
            return; 
        }
        
        if (['edit', 'renew'].includes(type)) {
            activeItemPathRef.current = poliza.path; 
            if (type === 'edit') setShowEditModal(true);
            if (type === 'renew') setShowRenewModal(true);
            
            await updateDoc(doc(db, poliza.path), { 
                bloqueo: { 
                    userId: user?.uid || 'sys', 
                    userName: profile?.userName || 'Usuario', 
                    timestamp: serverTimestamp() 
                } 
            }).catch(() => { activeItemPathRef.current = null; });
        }
    }, [user, profile]);

    const handleShowDebt = useCallback((poliza) => {
        setActivePoliza(poliza);
        setShowDebtModal(true);
    }, []);

    return {
        loading,
        needsHydration,
        clientsCache,
        activePoliza,
        showDetailModal,
        showEditModal,
        showRenewModal,
        showDebtModal,
        showFichaModal,
        tableState,
        itemsTable,
        activeItemPathRef,
        setShowDetailModal,
        setShowEditModal,
        setShowRenewModal,
        setShowDebtModal,
        setShowFichaModal,
        setActivePoliza,
        handleAction,
        handleShowDebt,
        unlockPoliza
    };
};